import { createContext, useContext } from "react";

import type { UnavailableProfileStyle } from "src/types";

/** Terminal result of a styling run. `null` while idle or in progress. */
export type ClassificationOutcome = "success" | "error";

type ErrorContextType = {
  classificationOutcome: ClassificationOutcome | null;
  classificationWarning: string | null;
  /**
   * True when the last recorded styling run failed, so there is nothing worth
   * reviewing. Mirrors a document setting, so it survives a taskpane refresh.
   * Deliberately excluded from `clearErrors` — that also runs on the Review
   * Styles path, which would otherwise unlock the button this flag gates.
   */
  lastStylingRunFailed: boolean;
  unavailableStyles: UnavailableProfileStyle[] | null;
  validationError: string | null;
  setClassificationOutcome: (value: ClassificationOutcome | null) => void;
  setClassificationWarning: (value: string | null) => void;
  setLastStylingRunFailed: (value: boolean) => void;
  setUnavailableStyles: (value: UnavailableProfileStyle[] | null) => void;
  setValidationError: (value: string | null) => void;
  /** Clears every error/outcome field at once, e.g. at the start of a new run. */
  clearErrors: () => void;
};

const ErrorContext = createContext<ErrorContextType>({} as ErrorContextType);

export const useErrorContext = () => {
  return useContext(ErrorContext);
};

export default ErrorContext;
